import { z } from "zod";
import { SCHEDULING_LIMITS, schedulingInstantSchema, schedulingTimeZoneSchema } from "./scheduling.js";
export const SCHEDULING_CONSENT_VERSION = 1;
export const SCHEDULING_DISCOVERY_CALENDARS = 200;
export const schedulingCalendarCapabilitySchema = z.enum(["read_busy", "create_event", "invite_guest"]);
const calendarId = z.string().min(1).max(256);
const accountId = z.string().min(1).max(2_048);
/** Owner-only discovery, never a guest-link response. Identity comes from the authenticated provider. */
export const schedulingConnectionDiscoverySchema = z.object({
    providerAccountId: accountId,
    accountLabel: z.string().min(1).max(200),
    observedAt: schedulingInstantSchema,
    complete: z.boolean(),
    calendars: z.array(z.object({
        id: calendarId,
        label: z.string().min(1).max(200),
        timeZone: schedulingTimeZoneSchema.optional(),
        capabilities: z.array(schedulingCalendarCapabilitySchema).max(3)
            .refine((items) => new Set(items).size === items.length, "Duplicate capability"),
    }).strict()).max(SCHEDULING_DISCOVERY_CALENDARS),
}).strict().refine((value) => new Set(value.calendars.map(({ id }) => id)).size === value.calendars.length, "Duplicate calendar identity");
/** A selected-calendar consent intent is separate from a request to publish a proposal. */
export const schedulingConsentSelectionSchema = z.object({
    expectedAccountId: accountId,
    destinationCalendarId: calendarId,
    conflictCalendarIds: z.array(calendarId).min(1).max(SCHEDULING_LIMITS.calendars),
    consentVersion: z.literal(SCHEDULING_CONSENT_VERSION),
}).strict().superRefine((value, ctx) => {
    if (new Set(value.conflictCalendarIds).size !== value.conflictCalendarIds.length) {
        ctx.addIssue({ code: "custom", path: ["conflictCalendarIds"], message: "Duplicate calendar identity" });
    }
    if (!value.conflictCalendarIds.includes(value.destinationCalendarId)) {
        ctx.addIssue({ code: "custom", path: ["conflictCalendarIds"], message: "Include the destination calendar" });
    }
});
export class SchedulingConnectionRefusal extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
    }
}
/** Server clock, verified identity and complete discovery are required before persisting a grant. */
export function assertSchedulingConsentSelection(discovery, selection, now) {
    const account = schedulingConnectionDiscoverySchema.parse(discovery);
    const requested = schedulingConsentSelectionSchema.parse(selection);
    const observed = Date.parse(account.observedAt);
    if (!Number.isSafeInteger(now) || !account.complete || observed > now || now - observed > SCHEDULING_LIMITS.availabilityMaxAgeMs) {
        throw new SchedulingConnectionRefusal("discovery_unavailable");
    }
    if (account.providerAccountId !== requested.expectedAccountId)
        throw new SchedulingConnectionRefusal("account_mismatch");
    const calendars = new Map(account.calendars.map((calendar) => [calendar.id, calendar]));
    if (requested.conflictCalendarIds.some((id) => !calendars.get(id)?.capabilities.includes("read_busy"))) {
        throw new SchedulingConnectionRefusal("calendar_unavailable");
    }
    const destination = calendars.get(requested.destinationCalendarId);
    if (!destination?.capabilities.includes("create_event") || !destination.capabilities.includes("invite_guest")) {
        throw new SchedulingConnectionRefusal("destination_unavailable");
    }
}
